import React, { useEffect, useMemo, useState } from "react";
import { apiClient } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useMastery } from "../context/MasteryContext";
import { useInstrument } from "../context/InstrumentContext";
import { useT } from "../context/LanguageContext";
import InstrumentTabs from "../components/InstrumentTabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import SongStatusToggle, { STATUSES } from "../components/SongStatusToggle";
import { toast } from "sonner";
import { Music, Plus, Trash2 } from "lucide-react";

function SheetMusicIcon({ className }) {
  return (
    <svg viewBox="0 0 40 40" className={className} aria-hidden>
      <rect x="6" y="4" width="28" height="32" rx="3" fill="#fff" stroke="#0a0a0a" strokeWidth="1.5" />
      {[0, 1, 2, 3, 4].map((i) => (
        <line key={i} x1="10" x2="30" y1={12 + i * 3} y2={12 + i * 3} stroke="#0a0a0a" strokeWidth="0.6" />
      ))}
      <ellipse cx="14" cy="22" rx="2.5" ry="1.8" transform="rotate(-20 14 22)" fill="#0a0a0a" />
      <line x1="16.2" y1="21" x2="16.2" y2="14" stroke="#0a0a0a" strokeWidth="1.2" />
      <ellipse cx="23" cy="18" rx="2.5" ry="1.8" transform="rotate(-20 23 18)" fill="#0a0a0a" />
      <line x1="25.2" y1="17" x2="25.2" y2="10" stroke="#0a0a0a" strokeWidth="1.2" />
      <path d="M16 14 Q21 12 25 10" stroke="#0a0a0a" strokeWidth="1.2" fill="none" />
    </svg>
  );
}

export default function SongsPage({ scope = "student" }) {
  const { user } = useAuth();
  const t = useT();
  const { celebrate } = useMastery() || {};
  const { current: currentInstrument, label: instLabel, emoji: instEmoji } = useInstrument();
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", composer: "", status: "just_started", notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get(`/songs?instrument=${encodeURIComponent(currentInstrument || "piano")}`);
      setSongs(r.data.songs);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [currentInstrument]);

  const add = async () => {
    if (!form.title.trim()) { toast.error(t("Title required")); return; }
    await apiClient.post("/songs", { ...form, instrument: currentInstrument || "piano" });
    setForm({ title: "", composer: "", status: "just_started", notes: "" });
    setOpen(false);
    toast.success(t("Song added")); load();
  };

  const updateStatus = async (song, status) => {
    const prevStatus = song.status;
    await apiClient.put(`/songs/${song.id}`, { ...song, status });
    setSongs((prev) => prev.map((s) => (s.id === song.id ? { ...s, status } : s)));
    if (status === "perfection" && prevStatus !== "perfection") {
      celebrate?.({ kind: "song", itemName: song.title });
    }
  };

  const del = async (song) => {
    if (!window.confirm(t("Delete song?"))) return;
    await apiClient.delete(`/songs/${song.id}`);
    setSongs((p) => p.filter((s) => s.id !== song.id));
  };

  return (
    <div className="space-y-6" data-testid="songs-page">
      <InstrumentTabs />
      {!currentInstrument && (
        <div className="bento-card p-10 text-center" data-testid="songs-no-instrument">
          <div className="text-5xl mb-3">🎼</div>
          <p className="font-display text-xl mb-2">{t("Add an instrument to start tracking songs.")}</p>
          <p className="text-sm mb-4" style={{ color: "var(--app-text-muted)" }}>
            {t("Pop over to")} <b>{t("My Instruments")}</b> {t("and tap \"Add instrument\" to get started.")}
          </p>
        </div>
      )}
      {currentInstrument && (<>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-4xl font-bold flex items-center gap-3"><Music className="w-8 h-8" /> {t("Songs I'm Learning")}</h1>
          <p style={{ color: "var(--app-text-muted)" }}>{t("Track what you're working on, now, next and always.")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="btn-pill btn-primary" data-testid="new-song-btn"><Plus className="w-4 h-4" /> {t("Add song")}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("New song")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t("Title *")}</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="song-title" /></div>
              <div><Label>{t("Composer")}</Label><Input value={form.composer} onChange={(e) => setForm({ ...form, composer: e.target.value })} /></div>
              <div>
                <Label>{t("Status")}</Label>
                <SongStatusToggle value={form.status} onChange={(v) => setForm({ ...form, status: v })} testIdPrefix="newsong-status" />
              </div>
              <Button onClick={add} className="btn-pill btn-primary w-full justify-center" data-testid="save-song">{t("Save")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <div>{t("Loading…")}</div> : songs.length === 0 ? (
        <div className="bento-card p-10 text-center">
          <div className="text-5xl mb-3">🎼</div>
          <p style={{ color: "var(--app-text-muted)" }}>{t("No songs yet. Add your first piece!")}</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {songs.map((s) => (
            <div key={s.id} className="bento-card p-4 flex items-start gap-3" data-testid={`song-${s.id}`}>
              <SheetMusicIcon className="w-12 h-12 flex-shrink-0" />
              <div className="flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-display font-bold">{s.title}</div>
                    {s.composer && <div className="text-sm" style={{ color: "var(--app-text-muted)" }}>{s.composer}</div>}
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => del(s)} data-testid={`delete-song-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
                <div className="mt-2">
                  <SongStatusToggle value={s.status} onChange={(v) => updateStatus(s, v)} testIdPrefix={`song-${s.id}-status`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </>)}
    </div>
  );
}
